import TextButton from '@components/Buttons/TextButton';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Grow, useTheme } from '@mui/material';
import {
	getSmartEdge,
	pathfindingJumpPointNoDiagonal,
	svgDrawSmoothLinePath
} from '@tisoap/react-flow-smart-edge';
import { getTaskColor } from '@utils/flows';
import React, {
	CSSProperties,
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import { BezierEdge, EdgeProps, Position, useReactFlow } from 'reactflow';
import { IEdgeData } from 'types/flows';
import { ITheme } from 'types/theme';

const PATH_DELETE_BUTTON_NUM_POINTS = 10;
const PATH_POINTS_MARGIN_PERCENT = 0.08;

const offsetPointsLength = (length: number) =>
	length - length * PATH_POINTS_MARGIN_PERCENT;

const getSVGPoints = (path?: SVGPathElement) => {
	const points: { x: number; y: number }[] = [];
	if (!path) return points;

	const len = offsetPointsLength(path.getTotalLength());
	for (let i = 1; i < PATH_DELETE_BUTTON_NUM_POINTS; i += 1) {
		const pt = path.getPointAtLength(
			(i * offsetPointsLength(len)) / (PATH_DELETE_BUTTON_NUM_POINTS - 1)
		);
		points.push({ x: pt.x - 13, y: pt.y - 14 });
	}
	return points;
};

const distance = (p1: { x: number; y: number }, p2: { x: number; y: number }) =>
	Math.abs(p2.x - p1.x) + Math.abs(p2.y - p1.y);

const foreignObjectSize = 30;
const ButtonEdge = ({
	id,
	sourceX,
	sourceY,
	targetX,
	targetY,
	data,
	sourcePosition,
	targetPosition,
	style = {},
	markerEnd
}: EdgeProps<IEdgeData>) => {
	const refInstance = useReactFlow();
	const [showDelete, setShowDelete] = useState(true);
	const [mousePosition, setMousePosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const theme = useTheme<ITheme>();
	const pathRef = useRef<SVGPathElement | null>(null);
	const flow = useReactFlow();
	const finalStyle: CSSProperties = {
		...style,
		strokeWidth: 1,
		stroke: theme.palette.text.disabled
	};
	if (data?.isBack) {
		finalStyle.stroke = theme.palette.text.disabled;
		finalStyle.strokeDasharray = '3 3 3 3';
	}
	if (data?.marked) {
		finalStyle.stroke = getTaskColor(theme, data?.nextTask?.type);
	}
	const deleteFunc = useCallback(
		(event: { stopPropagation: () => void }) => {
			event.stopPropagation();
			data?.onDelete(id);
		},
		[data, id]
	);

	const nodes = flow.getNodes();
	const getSmartEdgeResponse = useMemo(
		() =>
			getSmartEdge({
				sourcePosition,
				targetPosition,
				sourceX,
				sourceY,
				targetX,
				targetY,
				nodes,
				options: {
					drawEdge: svgDrawSmoothLinePath,
					generatePath: pathfindingJumpPointNoDiagonal,
					gridRatio: 10,
					nodePadding: 20
				}
			}),
		[nodes, sourcePosition, targetPosition, sourceX, sourceY, targetX, targetY]
	);

	const moveEvent = useCallback(
		(event: MouseEvent) => {
			// magic numbers due to a bug with react flow, should be in flow state manager.
			// see https://github.com/wbkd/react-flow/issues/2360.
			const newMousePosition = refInstance.project({
				x: event.clientX - 272,
				y: event.clientY - 115
			});
			const points = pathRef.current ? getSVGPoints(pathRef.current) : [];
			let position = {
				value: distance(
					{ x: points?.[0]?.x || 0, y: points?.[0]?.y || 0 },
					newMousePosition
				),
				selected: points?.[0]
			};
			points?.forEach((p) => {
				const d = distance({ x: p.x || 0, y: p.y }, newMousePosition);
				if (position.value > d) {
					position = { value: d, selected: p };
				}
			});
			if (position) setMousePosition(position.selected!);
		},
		[setMousePosition, pathRef, refInstance]
	);

	const onMouse = useCallback(
		(over: boolean) => () => {
			if (over) {
				pathRef.current?.addEventListener('mousemove', moveEvent);
			} else {
				pathRef.current?.removeEventListener('mousemove', moveEvent);
			}
			setShowDelete(over);

			return () => pathRef.current?.removeEventListener('mousemove', moveEvent);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setShowDelete, moveEvent, pathRef.current]
	);

	// If the value returned is null, it means "getSmartEdge" was unable to find
	// a valid path, and you should do something else instead
	if (getSmartEdgeResponse === null) {
		return (
			<BezierEdge
				id=""
				source=""
				target=""
				sourceX={0}
				sourceY={0}
				targetX={0}
				targetY={0}
				sourcePosition={Position.Left}
				targetPosition={Position.Left}
			/>
		);
	}
	const { svgPathString: edgePath } = getSmartEdgeResponse;

	return (
		<>
			<path
				style={finalStyle}
				className="react-flow__edge-path"
				d={edgePath}
				markerEnd={markerEnd}
			/>
			<path
				id={id}
				style={{
					...finalStyle,
					fill: 'none',
					stroke: 'transparent',
					strokeWidth: 20
				}}
				data-testid={id}
				aria-label=""
				d={edgePath}
				ref={pathRef}
				onMouseEnter={onMouse(true)}
				onMouseLeave={onMouse(false)}
				markerEnd={markerEnd}
			/>
			<foreignObject
				width={foreignObjectSize}
				height={foreignObjectSize}
				onMouseEnter={onMouse(true)}
				onMouseLeave={onMouse(false)}
				x={mousePosition?.x}
				y={mousePosition?.y}
				className="edgebutton-foreignobject"
				requiredExtensions="http://www.w3.org/1999/xhtml"
			>
				<Grow in={showDelete && !!mousePosition}>
					<Box zIndex={1000}>
						<TextButton
							data-testid={`delete-edge-button-${id}`}
							onClick={(e) => {
								deleteFunc(e);
							}}
						>
							<CancelIcon color="action" sx={{ width: 16, height: 16 }} />
						</TextButton>
					</Box>
				</Grow>
			</foreignObject>
		</>
	);
};

export default ButtonEdge;
